import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Save, X } from 'lucide-react';

import ActionButton from '../../../components/Buttons/ActionButton';
import InputField from '../../../components/Forms/Input/InputField';
import SimpleSearchSelect from '../../../components/Forms/Input/SimpleSearchSelect';
import FormModal from '../../../components/Modals/FormModal';
import QuickAddProduct from '../../../components/QuickAdd/Product/QuickAddProduct';
import MenuCategoryQuickField from '../menu/MenuCategoryQuickField';

import RestaurantMenuCategoryService from '../../../services/restaurant/RestaurantMenuCategoryService';
import RestaurantMenuItemService from '../../../services/restaurant/RestaurantMenuItemService';
import useApiErrorHandler from '../../../hooks/useApiErrorHandler';
import {
  MenuProductOption,
  RestaurantMenuCategoryDTO,
} from '../../../types/restaurant/RestaurantMenuTypes';

interface Props {
  isOpen: boolean;
  branchId: number;
  saving: boolean;
  onClose: () => void;
  onSubmit: (input: {
    productId: number;
    categoryId: number;
    displayName: string;
  }) => Promise<void>;
}

/**
 * Sub-modal para "＋ Crear ítem de menú" desde el form de receta. Crea el ítem
 * de menú (que envuelve un producto del catálogo) sin salir de la receta:
 * producto (con "＋ Nuevo"), categoría de menú (con "＋ nueva categoría"),
 * nombre visible opcional. El código del ítem lo genera el backend.
 */
const MenuItemQuickCreateModal = ({
  isOpen,
  branchId,
  saving,
  onClose,
  onSubmit,
}: Props) => {
  const { handle } = useApiErrorHandler();

  const [products, setProducts] = useState<MenuProductOption[]>([]);
  const [categories, setCategories] = useState<RestaurantMenuCategoryDTO[]>([]);
  const [productId, setProductId] = useState<number | ''>('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [displayName, setDisplayName] = useState('');
  const [touched, setTouched] = useState(false);
  const wasOpen = useRef(false);

  const reloadProducts = useCallback(async () => {
    try {
      setProducts(await RestaurantMenuItemService.products());
    } catch (error) {
      handle(error);
    }
  }, [handle]);

  const reloadCategories = useCallback(async () => {
    try {
      const response = await RestaurantMenuCategoryService.list(branchId, true);
      setCategories(response.data ?? []);
    } catch (error) {
      handle(error);
    }
  }, [branchId, handle]);

  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setProductId('');
      setCategoryId('');
      setDisplayName('');
      setTouched(false);
      void reloadProducts();
      void reloadCategories();
    }
    wasOpen.current = isOpen;
  }, [isOpen, reloadProducts, reloadCategories]);

  const createCategory = async (name: string) => {
    try {
      const response = await RestaurantMenuCategoryService.create(branchId, {
        name,
        description: '',
        icon: '',
        color: '',
        displayOrder: 1,
      });
      setCategories((current) => [...current, response.data]);
      return response.data;
    } catch (error) {
      handle(error);
      return null;
    }
  };

  const errors = useMemo(() => {
    const next: Record<string, string> = {};
    if (!productId) next.productId = 'Seleccione o cree un producto.';
    if (!categoryId) next.categoryId = 'Seleccione o cree una categoría.';
    return next;
  }, [productId, categoryId]);

  const hasErrors = Object.keys(errors).length > 0;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched(true);
    if (hasErrors || saving) return;
    await onSubmit({
      productId: Number(productId),
      categoryId: Number(categoryId),
      displayName: displayName.trim(),
    });
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={saving ? undefined : onClose}
      title="Crear ítem de menú"
      maxWidth="max-w-xl"
      zIndex="top"
    >
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-bodydark2 dark:text-bodydark">
          El precio y la imagen se toman del producto. El código del ítem se
          genera automáticamente a partir de la categoría.
        </p>

        <div>
          <SimpleSearchSelect
            label="Producto del catálogo"
            name="menuItemQuickProduct"
            selectedValue={productId || null}
            iconKey="product"
            placeholder="Seleccione un producto"
            searchPlaceholder="Buscar producto..."
            required
            disabled={saving}
            error={touched ? errors.productId : undefined}
            options={products.map((product) => ({
              id: product.id,
              name: product.category
                ? `${product.name} · ${product.category}`
                : product.name,
            }))}
            onSelect={(selected) => setProductId(selected.id)}
          />
          <QuickAddProduct onReload={reloadProducts} />
        </div>

        <MenuCategoryQuickField
          categories={categories}
          value={categoryId}
          error={touched ? errors.categoryId : undefined}
          disabled={saving}
          onChange={(id) => setCategoryId(id)}
          onCreateCategory={createCategory}
        />

        <InputField
          label="Nombre visible (opcional)"
          name="menuItemQuickDisplayName"
          type="text"
          iconKey="tag"
          value={displayName}
          placeholder="Si queda vacío se usa el nombre del producto"
          disabled={saving}
          onChange={(event) => setDisplayName(event.target.value)}
        />

        <div className="flex flex-col-reverse gap-3 border-t border-stroke pt-4 dark:border-strokedark sm:flex-row sm:justify-end">
          <ActionButton
            label="Cancelar"
            icon={<X className="h-4 w-4" />}
            variant="secondary"
            size="md"
            fullWidth={false}
            disabled={saving}
            onClick={onClose}
            className="w-full sm:w-auto"
          />
          <ActionButton
            label="Crear y usar"
            icon={<Save className="h-4 w-4" />}
            loading={saving}
            loadingLabel="Creando..."
            variant="primary"
            size="md"
            type="submit"
            fullWidth={false}
            disabled={saving || (touched && hasErrors)}
            className="w-full whitespace-nowrap sm:w-auto"
          />
        </div>
      </form>
    </FormModal>
  );
};

export default MenuItemQuickCreateModal;
